import { AI_STORAGE_PREFIX, MESSAGE_STATUS } from "./constants";

/**
 * Pure conversation state helpers for the persistent AI assistant.
 * No deletion APIs — new conversation only clears local UI context.
 */

export const initialConversationState = {
  isOpen: false,
  threadId: null,
  messages: [],
  draft: "",
  sending: false,
  restoring: false,
  error: null,
  ownerUserId: null,
};

export function createMessage({
  id,
  role,
  content,
  status = MESSAGE_STATUS.SENT,
  createdAt = new Date().toISOString(),
  retryPayload = null,
}) {
  return {
    id: id || `${role}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    role,
    content: content || "",
    status,
    createdAt,
    retryPayload,
  };
}

export function conversationReducer(state, action) {
  switch (action.type) {
    case "OPEN":
      return { ...state, isOpen: true };
    case "CLOSE":
      return { ...state, isOpen: false };
    case "TOGGLE":
      return { ...state, isOpen: !state.isOpen };
    case "SET_DRAFT":
      return { ...state, draft: action.draft ?? "" };
    case "SET_SENDING":
      return { ...state, sending: Boolean(action.sending) };
    case "SET_RESTORING":
      return { ...state, restoring: Boolean(action.restoring) };
    case "SET_ERROR":
      return { ...state, error: action.error ?? null };
    case "SET_THREAD":
      return { ...state, threadId: action.threadId || null };
    case "SET_MESSAGES":
      return { ...state, messages: Array.isArray(action.messages) ? action.messages : [] };
    case "APPEND_MESSAGE":
      return { ...state, messages: [...state.messages, action.message] };
    case "UPDATE_MESSAGE": {
      const { id, patch } = action;
      return {
        ...state,
        messages: state.messages.map((m) => (m.id === id ? { ...m, ...patch } : m)),
      };
    }
    case "REMOVE_MESSAGE":
      return {
        ...state,
        messages: state.messages.filter((m) => m.id !== action.id),
      };
    case "NEW_CONVERSATION":
      // Local UI reset only — never deletes stored backend records.
      return {
        ...state,
        threadId: null,
        messages: [],
        draft: "",
        sending: false,
        error: null,
      };
    case "HYDRATE": {
      const payload = action.payload || {};
      return {
        ...state,
        threadId: payload.threadId || null,
        messages: Array.isArray(payload.messages) ? payload.messages : [],
        draft: typeof payload.draft === "string" ? payload.draft : "",
        ownerUserId: payload.ownerUserId ?? state.ownerUserId,
        error: null,
        sending: false,
      };
    }
    case "BIND_OWNER":
      return {
        ...initialConversationState,
        isOpen: state.isOpen,
        ownerUserId: action.ownerUserId || null,
      };
    case "RESET_FOR_SECURITY":
      return { ...initialConversationState };
    default:
      return state;
  }
}

export function storageKeyForUser(userId) {
  const id = String(userId || "").trim();
  if (!id) return null;
  return `${AI_STORAGE_PREFIX}:${id}`;
}

export function loadPersistedConversation(userId) {
  const key = storageKeyForUser(userId);
  if (!key || typeof window === "undefined") return null;
  try {
    const raw = window.sessionStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.ownerUserId !== String(userId)) return null;
    return {
      threadId: parsed.threadId || null,
      messages: Array.isArray(parsed.messages) ? parsed.messages : [],
      draft: typeof parsed.draft === "string" ? parsed.draft : "",
      ownerUserId: String(userId),
    };
  } catch {
    return null;
  }
}

export function persistConversation(userId, snapshot) {
  const key = storageKeyForUser(userId);
  if (!key || typeof window === "undefined") return;
  try {
    const payload = {
      ownerUserId: String(userId),
      threadId: snapshot.threadId || null,
      messages: (snapshot.messages || []).map((m) => ({
        id: m.id,
        role: m.role,
        content: m.content,
        status: m.status === MESSAGE_STATUS.FAILED ? MESSAGE_STATUS.FAILED : MESSAGE_STATUS.SENT,
        createdAt: m.createdAt,
      })),
      draft: snapshot.draft || "",
      updatedAt: new Date().toISOString(),
    };
    window.sessionStorage.setItem(key, JSON.stringify(payload));
  } catch {
    // Ignore quota / private-mode failures — in-memory state still works.
  }
}

export function clearPersistedConversation(userId) {
  const key = storageKeyForUser(userId);
  if (!key || typeof window === "undefined") return;
  try {
    window.sessionStorage.removeItem(key);
  } catch {
    // ignore
  }
}

/** Strip tutor metadata markers from AI HTML responses. */
const TUTOR_MARKER = "<!--microlab-tutor:";

export function sanitizeTutorContent(content) {
  const raw = String(content || "");
  const index = raw.lastIndexOf(TUTOR_MARKER);
  if (index === -1) return raw;
  return raw.slice(0, index).trim();
}

/**
 * Map backend thread messages into assistant message shape.
 * @param {Array} raw
 */
export function mapThreadMessages(raw) {
  if (!Array.isArray(raw)) return [];
  return [...raw]
    .reverse()
    .map((m) =>
      createMessage({
        id: m._id || m.id,
        role: m?.user?._id === "ai" || m?.sender === "ai" ? "assistant" : "user",
        content: sanitizeTutorContent(m?.text || m?.content || ""),
        status: MESSAGE_STATUS.SENT,
        createdAt: m?.createdAt || m?.created_at,
      })
    );
}
